import { useEffect } from "react";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { gql } from "@/services/httpAdapter";
import { useQueryClient } from "@tanstack/react-query";

type SpecialUnitsPayload = {
	ids: string[];
	locationId: string;
};

type SubResult = { data?: { onUpdateManualSpecialUnitsFromSalesPerformance?: SpecialUnitsPayload } };

const useManualSpecialUnitsSubscription = (locationId: string) => {
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	useEffect(() => {
		if (!locationId) {
			return;
		}

		addSubscription(
			["updateManualSpecialUnitsFromSalesPerformance", locationId],
			[
				{
					context: "onUpdateManualSpecialUnitsFromSalesPerformance",
					query: gql`
						subscription onUpdateManualSpecialUnitsFromSalesPerformance($locationId: String) {
							onUpdateManualSpecialUnitsFromSalesPerformance(locationId: $locationId) {
								ids
								locationId
							}
						}
					`,
					variables: { locationId },
				},
			]
		);
	}, [locationId, addSubscription]);

	const sub = getSubscription(["updateManualSpecialUnitsFromSalesPerformance", locationId]);

	useEffect(() => {
		let cleanup: (() => void) | undefined;

		if (sub) {
			const cb = ({ data }: SubResult) => {
				const payload = data?.onUpdateManualSpecialUnitsFromSalesPerformance;
				if (!payload) {
					return;
				}

				const { ids = [] } = payload;

				queryClient.refetchQueries({ queryKey: ["specialUnitsSetupSidebar", locationId] });
				ids.forEach((id) => queryClient.invalidateQueries({ queryKey: ["specialUnitsSetupEntry", locationId, id] }));
			};

			sub.subscriptions?.onUpdateManualSpecialUnitsFromSalesPerformance?.setCallback(cb);

			cleanup = () => sub.subscriptions?.onUpdateManualSpecialUnitsFromSalesPerformance?.setCallback(undefined);
		}

		return cleanup;
	}, [sub, queryClient, locationId]);
};

export default useManualSpecialUnitsSubscription;
